import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import { useLazyQuery } from "@apollo/client";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";

// Custom.
import Wrapper from "../Content/Wrapper";
import Report from "../Content/Report";
import Row from "../Content/Row";
import Header from "../Content/Header";
import { GET_STATISTICS_DIPLOMAS } from "api";
import { GeneralObject } from "@vocollege/app/dist/global";
import VoLoader from "@vocollege/components/dist/VoLoader";
import Stack from "@mui/material/Stack";
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import {
  getStatisticsDataForDiplomas,
  getStatisticsTotal,
} from "services/Utils";
import { DownloadExcelButtonProps } from "@vocollege/components/dist/DownloadExcelButton";
import { getExcelData } from "./Utils";
import { getSummaryEducatorType, sortByLabel } from "../Utils";

const StatisticsDiplomas: React.FC = () => {
  const [parsedData2, setParsedData2] = useState<GeneralObject>({});
  const [parsedData, setParsedData] = useState<GeneralObject[]>([]);
  const [excelData, setExcelData] = useState<DownloadExcelButtonProps | null>(
    null
  );
  const [headerParams, setHeaderParams] = useState<GeneralObject>({});

  // Methods.

  const handleHeaderChange = (params: GeneralObject) => {
    setHeaderParams(params);
    loadQuery({
      variables: {
        from: params.from,
        to: params.to,
        groupBy: params.groupBy,
      },
    });
  };

  const getTitle = (item: GeneralObject) => {
    switch (headerParams?.groupBy) {
      case "region":
        return "title";

      default:
        return "title";
    }
  };

  // Api.

  const [loadQuery, { data, loading, variables }] = useLazyQuery(
    GET_STATISTICS_DIPLOMAS,
    {
      fetchPolicy: "network-only",
      onCompleted: (loadedData) => {
        setParsedData2(
          getStatisticsDataForDiplomas(
            loadedData?.certStatisticsDiplomas.data,
            headerParams?.groupBy
          )
        );
      },
    }
  );

  // Effects.

  useEffect(() => {
    if (data) {
      setExcelData(getExcelData(data, parsedData2, variables));
    }
  }, [parsedData2, data, variables]);

  return (
    <Wrapper title={I18n.get.diploma.labels.diplomas} hideTitleOnPrint>
      <Header
        filters={["from", "to", "groupBy"]}
        onChange={handleHeaderChange}
        excel={excelData || undefined}
      />
      {!loading && data && (
        <Box
          sx={{
            mt: 4,
            "@media print": {
              mt: 0,
            },
          }}
        >
          <Report
            title={I18n.get.diploma.label}
            date={data?.certStatisticsDiplomas.createdAt}
            interval={`${variables?.from} - ${variables?.to}`}
            summary={
              <Box>
                <Row
                  label={I18n.get.vocertStatistics.total}
                  // value={getStatisticsTotal(parsedData)}
                  value={parsedData2.total}
                />
                {Object.keys(parsedData2.itemsPerEducation)
                  .sort((a, b) =>
                    sortByLabel(a, b, parsedData2.itemsPerEducation)
                  )
                  .map((key: string) => (
                    <Row
                      key={key}
                      label={parsedData2.itemsPerEducation[key].label}
                      note={parsedData2.itemsPerEducation[key].note}
                      value={parsedData2.itemsPerEducation[key].value}
                      size="small"
                    />
                  ))}
                {/* {getSummary(parsedData).map((v: GeneralObject, i: number) => (
                  <Row
                    key={i}
                    label={v.label}
                    note={v.note}
                    value={v.value}
                    size="small"
                  />
                ))} */}
                <Divider variant="middle" sx={{ mt: 5 }} component="div">
                  <Typography component="h4" variant="h6" fontWeight={600}>
                    {I18n.get.vocertStatistics.perEducatorType}
                  </Typography>
                </Divider>
                <Typography
                  component="h6"
                  variant="caption"
                  marginBottom={3}
                  marginTop={1}
                  textAlign="center"
                  lineHeight={1.2}
                >
                  {I18n.get.vocertStatistics.perEducatorTypeNote}
                </Typography>
                {Object.keys(parsedData2.itemsPerEducatorType)
                  .sort((a, b) =>
                    sortByLabel(a, b, parsedData2.itemsPerEducatorType)
                  )
                  .map((key: string) => (
                    <Row
                      key={key}
                      label={parsedData2.itemsPerEducatorType[key].label}
                      note={parsedData2.itemsPerEducatorType[key].note}
                      value={parsedData2.itemsPerEducatorType[key].value}
                      size="small"
                    />
                  ))}
                {/* {getSummaryEducatorType(parsedData).map(
                  (v: GeneralObject, i: number) => (
                    <Row
                      key={i}
                      label={v.label}
                      note={v.note}
                      value={v.value}
                      size="small"
                    />
                  )
                )} */}
                <Divider variant="middle" sx={{ mt: 5 }} component="div">
                  <Typography component="h4" variant="h6" fontWeight={600}>
                    {I18n.trans(I18n.get.vocertStatistics.perItem, {
                      item: I18n.get.group.categories[
                        variables?.groupBy
                      ].toLowerCase(),
                    })}
                  </Typography>
                </Divider>
              </Box>
            }
          >
            <Stack>
              {Object.keys(parsedData2.items).map((itemId: string) => (
                <Box key={itemId} sx={{ mb: 2, "&:last-child": { mb: 0 } }}>
                  <Row
                    label={parsedData2.items[itemId].title}
                    value={parsedData2.items[itemId].total}
                  />
                  {Object.keys(parsedData2.items[itemId].children).map(
                    (childId: string) => {
                      const child = parsedData2.items[itemId].children[childId];
                      return (
                        <Box
                          key={childId}
                          sx={(theme) => ({
                            borderBottom: `3px solid ${theme.palette.grey[100]}`,
                            ml: headerParams?.groupBy === "region" ? 4 : 0,
                            mt: 2,
                            pb: 1,
                            "&:last-child": {
                              borderBottom: "none",
                            },
                          })}
                        >
                          {headerParams?.groupBy === "region" && (
                            <Row
                              label={child.title}
                              value={child.total}
                              size="small"
                            />
                          )}

                          {Object.keys(child.educations).map(
                            (educationId: string) => {
                              const education =
                                parsedData2.items[itemId].children[childId]
                                  .educations[educationId];

                              return (
                                <Box
                                  key={educationId}
                                  sx={{
                                    ml: 4,
                                  }}
                                >
                                  <Row
                                    label={
                                      I18n.get.diploma.diplomaTypes[
                                        education.diploma_type
                                      ]
                                    }
                                    value={education.total}
                                    note={
                                      education.diploma_type !== "yh" &&
                                      I18n.get.diploma.education[
                                        education.diploma_type
                                      ][education.education]
                                    }
                                    size="small"
                                  />

                                  {headerParams?.groupBy !== "educator" &&
                                    Object.keys(education.educators).map(
                                      (educatorId: string) => {
                                        const educator =
                                          education.educators[educatorId];

                                        return (
                                          <Box key={educatorId} sx={{ ml: 4 }}>
                                            <Row
                                              label={educator.title}
                                              value={educator.total}
                                              size="small"
                                            />
                                          </Box>
                                        );
                                      }
                                    )}
                                </Box>
                              );
                            }
                          )}
                        </Box>
                      );
                    }
                  )}
                </Box>
              ))}
              {false &&
                parsedData.map((v: GeneralObject, i: number) => (
                  <Box key={i} sx={{ mb: 2, "&:last-child": { mb: 0 } }}>
                    <Row key={i} label={v[getTitle(v)]} value={v.total} />
                    {v.items.map((v: GeneralObject, i: number) => (
                      <Box
                        key={i}
                        sx={{
                          ml: 4,
                          mt: 2,
                        }}
                      >
                        {headerParams?.groupBy === "region" && (
                          <Row
                            label={v.local_title}
                            value={v.total}
                            size="small"
                          />
                        )}

                        <Box sx={{ ml: 4 }}>
                          <Row
                            label={
                              I18n.get.diploma.diplomaTypes[v.diploma_type]
                            }
                            value={v.total}
                            note={
                              v.diploma_type !== "yh" &&
                              I18n.get.diploma.education[v.diploma_type][
                                v.education
                              ]
                            }
                            size="small"
                          />
                          {headerParams?.groupBy === "region" && (
                            <Box sx={{ ml: 4 }}>
                              <Row
                                label={v.educator_title}
                                value={v.total}
                                size="small"
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ))}
            </Stack>
          </Report>
        </Box>
      )}
      {loading && <VoLoader />}
    </Wrapper>
  );
};

export default StatisticsDiplomas;
